import type { IconName } from "@evolua/ui";

export type NavGroupId = "me" | "team" | "hr" | "org" | "admin";

export type NavItem = {
  id: string;
  label: string;
  path: string;
  permissions?: readonly string[];
  mobile?: boolean;
  badge?: number;
  group?: NavGroupId;
  icon?: IconName;
};

export const NAV_GROUPS: { id: NavGroupId; label: string }[] = [
  { id: "me", label: "Meu espaço" },
  { id: "team", label: "Minha equipe" },
  { id: "hr", label: "RH" },
  { id: "org", label: "Organização" },
  { id: "admin", label: "Administração" },
];

/**
 * Destinos do app único (D-018). Menu filtrado por permissões —
 * o bloqueio real continua no backend.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  { id: "home", label: "Início", path: "/", mobile: true, group: "me", icon: "home" },
  {
    id: "payroll",
    label: "Holerite",
    path: "/holerite",
    permissions: ["payroll:read"],
    mobile: true,
    group: "me",
    icon: "payslip",
  },
  {
    id: "vacation",
    label: "Férias",
    path: "/ferias",
    permissions: ["vacation:read"],
    mobile: true,
    group: "me",
    icon: "calendar",
  },
  {
    id: "timecard",
    label: "Ponto",
    path: "/ponto",
    permissions: ["timecard:read"],
    mobile: true,
    group: "me",
    icon: "clock",
  },
  {
    id: "my-requests",
    label: "Pedidos",
    path: "/minhas-solicitacoes",
    permissions: ["request:write"],
    mobile: true,
    group: "me",
    icon: "inbox",
  },
  {
    id: "notifications",
    label: "Avisos",
    path: "/notificacoes",
    permissions: ["lgpd:export_own"],
    group: "me",
    icon: "bell",
  },
  {
    id: "documents",
    label: "Documentos",
    path: "/documentos",
    permissions: ["document:read"],
    mobile: true,
    group: "me",
    icon: "document",
  },
  {
    id: "announcements",
    label: "Comunicados",
    path: "/comunicados",
    permissions: ["lgpd:export_own"],
    group: "me",
    icon: "megaphone",
  },
  {
    id: "benefits",
    label: "Benefícios",
    path: "/beneficios",
    permissions: ["benefit:read"],
    group: "me",
    icon: "gift",
  },
  {
    id: "team",
    label: "Equipe",
    path: "/equipe",
    // Colaborador também tem employee:read + org:read; approve marca escopo de gestor.
    permissions: ["vacation:approve"],
    group: "team",
    icon: "users",
  },
  {
    id: "approvals",
    label: "Aprovações",
    path: "/aprovacoes",
    permissions: ["vacation:approve"],
    group: "team",
    icon: "check-circle",
  },
  {
    id: "vacation-calendar",
    label: "Calendário",
    path: "/calendario-ferias",
    permissions: ["vacation:approve"],
    group: "team",
    icon: "calendar",
  },
  {
    id: "team-indicators",
    label: "Indicadores",
    path: "/indicadores-equipe",
    permissions: ["vacation:approve"],
    group: "team",
    icon: "chart",
  },
  {
    id: "requests",
    label: "Solicitações",
    path: "/solicitacoes",
    // request:read também no autoatendimento; resolve = fila de gestor/RH.
    permissions: ["request:resolve"],
    group: "hr",
    icon: "inbox",
  },
  {
    id: "hr-vacations",
    label: "Férias RH",
    path: "/ferias-rh",
    permissions: ["vacation:approve_on_behalf"],
    group: "hr",
    icon: "calendar",
  },
  {
    id: "hr-documents",
    label: "Docs RH",
    path: "/documentos-rh",
    permissions: ["document:write"],
    group: "hr",
    icon: "folder",
  },
  {
    id: "hr-announcements",
    label: "Comunicados RH",
    path: "/comunicados-rh",
    permissions: ["document:write"],
    group: "hr",
    icon: "megaphone",
  },
  {
    id: "hr-admissions",
    label: "Admissões",
    path: "/admissoes-rh",
    permissions: ["document:write"],
    group: "hr",
    icon: "user",
  },
  {
    id: "hr-offboardings",
    label: "Desligamentos",
    path: "/desligamentos-rh",
    permissions: ["document:write"],
    group: "hr",
    icon: "log-out",
  },
  {
    id: "people",
    label: "Pessoas",
    path: "/pessoas",
    // employee:write também existe no colaborador (autoatendimento); export é RH.
    permissions: ["employee:read", "employee:export"],
    group: "hr",
    icon: "users",
  },
  {
    id: "org",
    label: "Estrutura",
    path: "/estrutura",
    permissions: ["org:read"],
    group: "org",
    icon: "sitemap",
  },
  {
    id: "orgchart",
    label: "Organograma",
    path: "/organograma",
    permissions: ["org:read"],
    group: "org",
    icon: "sitemap",
  },
  {
    id: "integrations",
    label: "Integrações",
    path: "/integracoes",
    permissions: ["integration:read"],
    group: "org",
    icon: "plug",
  },
  {
    id: "flags",
    label: "Flags",
    path: "/configuracoes/flags",
    permissions: ["settings:manage"],
    group: "admin",
    icon: "flag",
  },
  {
    id: "account",
    label: "Conta",
    path: "/conta",
    mobile: true,
    group: "me",
    icon: "user",
  },
];

export function filterNavItems(
  items: readonly NavItem[],
  permissions: readonly string[],
): NavItem[] {
  return items.filter((item) => {
    if (!item.permissions?.length) return true;
    return item.permissions.every((p) => permissions.includes(p));
  });
}
